import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import { ChipInput, GridInfoFooter } from 'components/common';
import { CreativePreviewCell } from 'features/facebook';
import { useEnqueueSnackbar } from 'hooks';
import { getApiErrorMessage } from 'lib/api';
import datadogActions from 'lib/datadog';
import { Network } from '../../../../constants';
import type {
  TwitterCreativeFlow,
  TwitterCreativePriority,
  TwitterCreativeSettings,
  UpdateTwitterCreativeSettingsParams,
} from '../api';
import { useTwitterCreativeSettings, useTwitterFlows, useUpdateTwitterCreativeSettings } from '../api';
import { useNameQueryParam, usePendingValues, useSortParams } from '../hooks';
import { toSortParams } from '../utils';
import FlowsSelect from './FlowsSelect';
import LaunchedStatusIcon from './LaunchedStatusIcon';
import PrioritySelect from './PrioritySelect';
import UseInAutomationSwitch from './UseInAutomationSwitch';
import XCreativesGridSkeleton from './XCreativesGridSkeleton';

const EMPTY_ROWS: TwitterCreativeSettings[] = [];
const EMPTY_FLOWS: TwitterCreativeFlow[] = [];
const EMPTY_QUERY: string[] = [];

interface SearchFormValues {
  filterCreativeByName: string[];
}

/** Key of a row in the pending maps: the settings row id. */
const rowKey = (row: TwitterCreativeSettings): string => String(row.settings_id);

const LEADING_COLUMNS: GridColDef<TwitterCreativeSettings>[] = [
  {
    field: 'thumbnail_url',
    headerName: 'Preview',
    width: 80,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params: GridRenderCellParams<TwitterCreativeSettings>) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
        <CreativePreviewCell previewLink={params.row.thumbnail_url} name={params.row.name} />
      </Box>
    ),
  },
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 320, sortable: false },
  {
    field: 'account_name',
    headerName: 'Account',
    width: 260,
    sortable: false,
    valueGetter: (_value: unknown, row: TwitterCreativeSettings) => `${row.account_name} (${row.account_id})`,
  },
  {
    field: 'created',
    headerName: 'Created',
    width: 120,
    valueFormatter: (value: string) => value.slice(0, 10),
  },
];

const TYPE_COLUMN: GridColDef<TwitterCreativeSettings> = { field: 'creative_type', headerName: 'Type', width: 100 };

const STATUS_COLUMN: GridColDef<TwitterCreativeSettings> = {
  field: 'is_launched',
  headerName: 'Status',
  width: 90,
  sortable: false,
  align: 'center',
  headerAlign: 'center',
  renderCell: (params: GridRenderCellParams<TwitterCreativeSettings>) => (
    <LaunchedStatusIcon launched={params.row.is_launched} />
  ),
};

interface XCreativesTableProps {
  /** App code: limits the creatives, and the flows to pick from and to filter by. */
  app: string;
}

/**
 * X creatives with their settings. Rows are sorted by the backend (`sort` / `order`), creatives used in
 * automation (`use_in_automation`) first by default.
 */
const XCreativesTable: FC<XCreativesTableProps> = ({ app }) => {
  const { onShowAlert, onShowErrorAlert } = useEnqueueSnackbar();
  const [flowFilter, setFlowFilter] = useState<string[]>([]);
  const { sortModel, setSortModel } = useSortParams();
  const { query: urlQuery, setQuery: setUrlQuery } = useNameQueryParam();
  const searchForm = useForm<SearchFormValues>({ defaultValues: { filterCreativeByName: urlQuery } });
  const searchQuery = useWatch({ control: searchForm.control, name: 'filterCreativeByName' }) ?? EMPTY_QUERY;

  useEffect(() => {
    setUrlQuery(searchQuery);
  }, [searchQuery, setUrlQuery]);

  const params = useMemo(
    () => ({
      app,
      ...(searchQuery.length ? { query: searchQuery } : {}),
      ...(flowFilter.length ? { flow: flowFilter } : {}),
      ...toSortParams(sortModel),
    }),
    [app, flowFilter, searchQuery, sortModel],
  );
  const { data, isLoading, isError, error } = useTwitterCreativeSettings({ params });
  const rows = data ?? EMPTY_ROWS;

  const { data: flowsData, isLoading: isFlowsLoading } = useTwitterFlows({ params: { app } });
  const flows = flowsData ?? EMPTY_FLOWS;
  const flowNames = useMemo(() => [...new Set(flows.map((flow) => flow.name))], [flows]);

  const footer = useMemo(
    // eslint-disable-next-line react/display-name
    () => () => (
      <GridInfoFooter gap={0.5}>
        <Typography variant="body2">{rows.length} found</Typography>
      </GridInfoFooter>
    ),
    [rows.length],
  );

  const { mutate: updateSettings } = useUpdateTwitterCreativeSettings();
  const { values: pendingPriorities, start: startPriority, finish: finishPriority } =
    usePendingValues<TwitterCreativePriority>();
  const {
    values: pendingUseInAutomation,
    start: startUseInAutomation,
    finish: finishUseInAutomation,
  } = usePendingValues<boolean>();
  const { values: pendingFlows, start: startFlows, finish: finishFlows } = usePendingValues<TwitterCreativeFlow[]>();

  const save = useCallback(
    (
      variables: UpdateTwitterCreativeSettingsParams,
      messages: { success: string; error: string },
      onSettled: () => void,
    ) => {
      updateSettings(variables, {
        onSuccess: () => {
          onShowAlert(messages.success);
        },
        onError: (err) => {
          onShowErrorAlert(getApiErrorMessage(err, messages.error), { delayAlert: 3500 });
        },
        onSettled,
      });
    },
    [onShowAlert, onShowErrorAlert, updateSettings],
  );

  const handlePriorityChange = useCallback(
    (row: TwitterCreativeSettings, priority: TwitterCreativePriority) => {
      startPriority(rowKey(row), priority);
      save(
        { settings_id: row.settings_id, priority },
        { success: 'Priority updated', error: 'Failed to update priority' },
        () => {
          finishPriority(rowKey(row));
        },
      );
    },
    [finishPriority, save, startPriority],
  );

  const handleUseInAutomationChange = useCallback(
    (row: TwitterCreativeSettings, useInAutomation: boolean) => {
      startUseInAutomation(rowKey(row), useInAutomation);
      save(
        { settings_id: row.settings_id, use_in_automation: useInAutomation },
        { success: 'Use in automation updated', error: 'Failed to update use in automation' },
        () => {
          finishUseInAutomation(rowKey(row));
        },
      );
    },
    [finishUseInAutomation, save, startUseInAutomation],
  );

  const handleFlowsChange = useCallback(
    (row: TwitterCreativeSettings, nextFlows: TwitterCreativeFlow[]) => {
      startFlows(rowKey(row), nextFlows);
      save(
        { settings_id: row.settings_id, flows: nextFlows },
        { success: 'Flows updated', error: 'Failed to update flows' },
        () => {
          finishFlows(rowKey(row));
        },
      );
    },
    [finishFlows, save, startFlows],
  );

  const columns = useMemo<GridColDef<TwitterCreativeSettings>[]>(
    () => [
      ...LEADING_COLUMNS,
      {
        field: 'flows',
        headerName: 'Flow',
        width: 320,
        sortable: false,
        renderCell: (params: GridRenderCellParams<TwitterCreativeSettings>) => (
          <FlowsSelect
            creativeName={params.row.name}
            value={pendingFlows[rowKey(params.row)] ?? params.row.flows}
            options={flows}
            optionsLoading={isFlowsLoading}
            loading={rowKey(params.row) in pendingFlows}
            onCommit={(nextFlows) => {
              handleFlowsChange(params.row, nextFlows);
            }}
          />
        ),
      },
      TYPE_COLUMN,
      {
        field: 'use_in_automation',
        headerName: 'Use in automation',
        width: 160,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<TwitterCreativeSettings>) => (
          <UseInAutomationSwitch
            creativeName={params.row.name}
            checked={pendingUseInAutomation[rowKey(params.row)] ?? params.row.use_in_automation}
            loading={rowKey(params.row) in pendingUseInAutomation}
            onChange={(checked) => {
              handleUseInAutomationChange(params.row, checked);
            }}
          />
        ),
      },
      STATUS_COLUMN,
      {
        field: 'priority',
        headerName: 'Priority',
        width: 130,
        renderCell: (params: GridRenderCellParams<TwitterCreativeSettings>) => (
          <PrioritySelect
            creativeName={params.row.name}
            value={pendingPriorities[rowKey(params.row)] ?? params.row.priority}
            loading={rowKey(params.row) in pendingPriorities}
            onChange={(priority) => {
              handlePriorityChange(params.row, priority);
            }}
          />
        ),
      },
    ],
    [
      flows,
      handleFlowsChange,
      handlePriorityChange,
      handleUseInAutomationChange,
      isFlowsLoading,
      pendingFlows,
      pendingPriorities,
      pendingUseInAutomation,
    ],
  );

  useEffect(() => {
    if (!isError || !error) return;

    datadogActions.trackDatadogError(error as Error, {
      source: 'twitter_upload_creatives_x_creatives_query',
      network: Network.X,
    });
  }, [isError, error]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, width: '100%' }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
        <FormProvider {...searchForm}>
          <Box sx={{ flex: 1, maxWidth: 560 }}>
            <ChipInput name="filterCreativeByName" inputLabel="Search by name" />
          </Box>
        </FormProvider>
        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          options={flowNames}
          loading={isFlowsLoading}
          value={flowFilter}
          onChange={(_event, value) => {
            setFlowFilter(value);
          }}
          renderInput={(inputParams) => <TextField {...inputParams} label="Filter by flow" />}
          sx={{ flex: 1, maxWidth: 560 }}
        />
      </Box>
      {isError ? (
        <Typography variant="body2" color="error" role="alert">
          Failed to load X creatives
        </Typography>
      ) : null}
      {isLoading ? (
        <XCreativesGridSkeleton />
      ) : (
        <Box sx={{ flex: 1, width: '100%', minHeight: 400, position: 'relative' }}>
          <Box sx={{ position: 'absolute', inset: 0, overflow: 'auto' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row.settings_id}
              disableRowSelectionOnClick
              hideFooterPagination
              sortingMode="server"
              sortingOrder={['asc', 'desc']}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              slots={{ footer }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default XCreativesTable;
